import { useCallback, useEffect, useRef, useState } from "react";
import { SpeechBubbleToast } from "@ever-tutor/et-ui";
import { useNotepadStore } from "../store/useNotepadStore";
import confettiBallUrl from "/assets/confetti-ball.svg";

const TOAST_DURATION = 5;

const CloseNotepadIcon = () => (
  <svg
    width="46"
    height="46"
    viewBox="0 0 38 38"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect width="38" height="38" rx="19" fill="#E2FFE3" />
    <image href={confettiBallUrl} x="7.5" y="7.5" width="24" height="24" />
  </svg>
);

export function CloseNotepadToast({
  blobBottom,
}: { blobBottom?: number } = {}) {
  const [visible, setVisible] = useState(false);
  const [countdown, setCountdown] = useState(TOAST_DURATION);
  const [progressKey, setProgressKey] = useState(0);
  const pendingActionRef = useRef<(() => void) | null>(null);
  const close = useNotepadStore((s) => s.close);
  const cancelCloseCountdown = useNotepadStore((s) => s.cancelCloseCountdown);

  const showToast = useCallback(() => {
    setCountdown(TOAST_DURATION);
    setProgressKey((k) => k + 1);
    setVisible(true);
  }, []);

  const cancelClose = useCallback(() => {
    pendingActionRef.current = null;
    setVisible(false);
    cancelCloseCountdown();
  }, [cancelCloseCountdown]);

  // Show when store enters CLOSING_COUNTDOWN — TldrawCanvasPanel already delays
  // this transition to the correct audio timestamp, so we appear at the right time.
  useEffect(() => {
    return useNotepadStore.subscribe((state, prev) => {
      if (
        state.state === "CLOSING_COUNTDOWN" &&
        prev.state !== "CLOSING_COUNTDOWN"
      ) {
        showToast();
      }
    });
  }, [showToast]);

  useEffect(() => {
    window.addEventListener("notepad:cancel-close", cancelClose);
    return () =>
      window.removeEventListener("notepad:cancel-close", cancelClose);
  }, [cancelClose]);

  useEffect(() => {
    if (!visible) return;
    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          pendingActionRef.current = () => close();
          setVisible(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [visible, close]);

  const handleExitComplete = useCallback(() => {
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    action?.();
  }, []);

  return (
    <SpeechBubbleToast
      open={visible}
      top={blobBottom}
      width={320}
      progressDurationS={TOAST_DURATION}
      progressKey={progressKey}
      onExitComplete={handleExitComplete}
      aria-label="Closing notepad notice"
    >
      <div
        style={{
          padding: "17px 17px 14px 17px",
          display: "flex",
          flexDirection: "column",
          gap: 14,
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
          <div style={{ flexShrink: 0, alignSelf: "center" }}>
            <CloseNotepadIcon />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p
              style={{
                margin: 0,
                fontSize: 16,
                fontWeight: 700,
                color: "#1a1a1a",
                lineHeight: 1.35,
              }}
            >
              {`Closing Notepad in ${countdown}`}
            </p>
            <p
              style={{
                margin: "5px 0 0",
                fontSize: 13,
                fontWeight: 400,
                color: "#1a1a1a",
              }}
            >
              Nice work! If you still have questions, hold S and ask the tutor.
            </p>
          </div>
        </div>
      </div>
    </SpeechBubbleToast>
  );
}
